import { Component } from "inferno";
import { Link } from "inferno-router";
import {
  Community,
  CommunityFollowerState,
  CommunityView,
  MyUserInfo,
} from "lemmy-js-client";
import { userNotLoggedInOrBanned } from "@utils/app";
import { getQueryString, numToSI } from "@utils/helpers";
import { I18NextService } from "../../services";
import { aimooT } from "@utils/aimoo-i18n";
import { Icon, Spinner } from "../common/icon";
import { PictrsImage } from "../common/pictrs-image";
import { LoadingEllipses } from "../common/loading-ellipses";
import classNames from "classnames";
import { communityLink } from "./community-link";

const AVATAR_GRADIENTS = [
  "aimoo-avatar-1",
  "aimoo-avatar-2",
  "aimoo-avatar-3",
  "aimoo-avatar-4",
  "aimoo-avatar-5",
  "aimoo-avatar-6",
] as const;

function avatarGradientClass(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash + name.charCodeAt(i) * (i + 1)) % AVATAR_GRADIENTS.length;
  }
  return AVATAR_GRADIENTS[hash] ?? AVATAR_GRADIENTS[0];
}

type CommunityHeaderProps = {
  community?: Community;
  communityView?: CommunityView;
  urlCommunityName?: string;
  myUserInfo: MyUserInfo | undefined;
  followLoading?: boolean;
  canPost?: boolean;
  categoryName?: string;
  onFollow?: () => void;
  onUnFollow?: () => void;
};

export class CommunityHeader extends Component<CommunityHeaderProps> {
  render() {
    const {
      community,
      communityView,
      urlCommunityName,
      myUserInfo,
      followLoading,
      canPost,
      categoryName,
      onFollow,
      onUnFollow,
    } = this.props;

    const title = community?.title ?? urlCommunityName ?? "";
    const initial = title.trim().charAt(0).toUpperCase() || "?";
    const followState = communityView?.community_actions?.follow_state as
      | CommunityFollowerState
      | undefined;
    const joined = followState === "accepted";
    const pending = followState === "pending";
    const banned = !!communityView?.community_actions?.received_ban_at;
    const isOwnerOrMod = !!communityView?.can_mod;
    const createdAt = (
      community as { published_at?: string; published?: string } | undefined
    )?.published_at?.substring(0, 10) ??
      (community as { published?: string } | undefined)?.published?.substring(
        0,
        10,
      );
    const createPostLink = community
      ? "/create_post" +
        getQueryString({ communityId: community.id.toString() })
      : "/create_post";
    const canCreatePost =
      canPost &&
      community &&
      !community.deleted &&
      !community.removed &&
      !userNotLoggedInOrBanned(myUserInfo);

    return (
      <div className="aimoo-community-detail__header">
        <div className="aimoo-community-detail__banner">
          {community?.banner && (
            <PictrsImage src={community.banner} type="banner" viewer />
          )}
        </div>
        <div className="aimoo-community-detail__header-body">
          <div className="aimoo-community-detail__header-row">
            {community?.icon ? (
              <div className="aimoo-community-detail__avatar">
                <PictrsImage
                  src={community.icon}
                  type="icon"
                  nsfw={community.nsfw}
                />
              </div>
            ) : (
              <div
                className={classNames(
                  "aimoo-community-detail__avatar aimoo-community-detail__avatar--fallback",
                  community
                    ? avatarGradientClass(community.name)
                    : "aimoo-avatar-1",
                )}
              >
                {initial}
              </div>
            )}

            <div className="aimoo-community-detail__header-main">
              <div className="aimoo-community-detail__title-row">
                <h1 className="aimoo-community-detail__title">
                  {community?.title ?? (
                    <>
                      {urlCommunityName}
                      <LoadingEllipses />
                    </>
                  )}
                </h1>
                {community?.posting_restricted_to_mods && (
                  <Icon icon="lock" inline classes="text-danger" />
                )}
                {(community as { is_official?: boolean })?.is_official && (
                  <span className="aimoo-community-detail__badge">
                    {aimooT("official")}
                  </span>
                )}
                {isOwnerOrMod && (
                  <span className="aimoo-community-detail__badge aimoo-community-detail__badge--owner">
                    {aimooT("created_by_me")}
                  </span>
                )}
              </div>
              {community?.summary && (
                <p className="aimoo-community-detail__summary">
                  {community.summary}
                </p>
              )}
              {community && (
                <div className="aimoo-community-detail__meta">
                  <span className="aimoo-community-detail__meta-item">
                    <Icon icon="groups" classes="icon-inline" />
                    {aimooT("members_count", {
                      count: numToSI(community.subscribers),
                    })}
                  </span>
                  {createdAt && (
                    <span>{aimooT("created_on", { date: createdAt })}</span>
                  )}
                  {categoryName && <span>{categoryName}</span>}
                </div>
              )}
            </div>

            {community && !banned && (
              <div className="aimoo-community-detail__actions">
                {myUserInfo && !isOwnerOrMod && (
                  <button
                    type="button"
                    className={classNames("aimoo-community-detail__btn", {
                      "aimoo-community-detail__btn--joined": joined || pending,
                      "aimoo-community-detail__btn--primary":
                        !joined && !pending,
                    })}
                    disabled={followLoading}
                    onClick={() =>
                      !followState ? onFollow?.() : onUnFollow?.()
                    }
                  >
                    {followLoading ? (
                      <Spinner />
                    ) : pending ? (
                      aimooT("pending")
                    ) : joined ? (
                      aimooT("joined")
                    ) : (
                      aimooT("join")
                    )}
                  </button>
                )}
                {!myUserInfo && (
                  <Link
                    className="aimoo-community-detail__btn aimoo-community-detail__btn--outline"
                    to="/login"
                  >
                    {I18NextService.i18n.t("subscribe")}
                  </Link>
                )}
                {isOwnerOrMod && community && (
                  <Link
                    className="aimoo-community-detail__btn aimoo-community-detail__btn--outline"
                    to={`${communityLink(community).link}/settings`}
                  >
                    {aimooT("manage")}
                  </Link>
                )}
                {canCreatePost && (
                  <Link
                    className="aimoo-community-detail__btn aimoo-community-detail__btn--primary"
                    to={createPostLink}
                  >
                    {aimooT("publish_post")}
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}
